import React from 'react';
import { NxTheme, withAlpha } from './theme';
import { NxIcon } from './Icons';

// ── Monogram ──
export function Monogram({ size = 32, bg, fg }: { size?: number; bg: string; fg: string }) {
  return (
    <div style={{
      width: size, height: size,
      borderRadius: size * 0.28,
      background: bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: fg,
      fontFamily: '"Geist", ui-sans-serif, system-ui, sans-serif',
      fontWeight: 600, fontSize: size * 0.42,
      letterSpacing: '-0.02em', flexShrink: 0,
    }}>nx</div>
  );
}

// ── Wordmark ──
export function Wordmark({ t, compact, color }: { t: NxTheme; compact?: boolean; color?: string }) {
  const fg = color || t.ink;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <Monogram size={compact ? 26 : 30} bg={fg} fg={!t.dark ? '#0a0b0d' : '#fafaf8'} />
      {!compact && (
        <span style={{
          fontFamily: '"Geist", ui-sans-serif, sans-serif',
          fontWeight: 500, fontSize: 16,
          letterSpacing: '-0.01em', color: fg,
        }}>nixassign</span>
      )}
    </div>
  );
}

// ── Field ──
interface FieldProps {
  t: NxTheme;
  label: string;
  name?: string;
  value?: string;
  placeholder?: string;
  type?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  options?: { value: string; label: string }[];
  helper?: React.ReactNode;
  error?: boolean;
  w?: string | number;
  disabled?: boolean;
}

export function NxField({ t, label, name, value, placeholder, type = 'text', onChange, options, helper, error, w, disabled }: FieldProps) {
  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: 11, fontWeight: 500,
    letterSpacing: '0.04em', textTransform: 'uppercase',
    color: t.inkMute, marginBottom: 6,
  };
  const inputStyle: React.CSSProperties = {
    width: '100%', height: 44, padding: '0 14px',
    boxSizing: 'border-box', background: t.surface,
    border: `1px solid ${error ? t.danger : t.border}`,
    borderRadius: 8, fontFamily: 'inherit', fontSize: 14,
    color: t.ink, outline: 'none',
  };

  let control;
  if (options) {
    control = (
      <select
        name={name}
        value={value}
        onChange={onChange as any}
        disabled={disabled}
        style={{ ...inputStyle, appearance: 'none', cursor: 'pointer', paddingRight: 32 }}
      >
        <option value="">{placeholder || 'Select...'}</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  } else {
    control = (
      <input
        type={type}
        name={name}
        value={value}
        placeholder={placeholder}
        onChange={onChange as any}
        disabled={disabled}
        style={inputStyle}
      />
    );
  }

  return (
    <div style={{ width: w || '100%' }}>
      <label style={labelStyle}>{label}</label>
      {control}
      {helper && <div style={{ fontSize: 12, color: error ? t.danger : t.inkMute, marginTop: 6 }}>{helper}</div>}
    </div>
  );
}

// ── Button ──
interface ButtonProps {
  t: NxTheme;
  kind?: 'primary' | 'secondary' | 'ghost';
  children: React.ReactNode;
  w?: string | number;
  icon?: React.ReactNode;
  danger?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  disabled?: boolean;
  type?: 'button' | 'submit';
}

export function NxButton({ t, kind = 'primary', children, w, icon, danger, onClick, disabled, type = 'button' }: ButtonProps) {
  const base: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    gap: 8, height: 44, padding: '0 18px', borderRadius: 8,
    fontFamily: 'inherit', fontSize: 14, fontWeight: 500,
    letterSpacing: '-0.005em', cursor: disabled ? 'not-allowed' : 'pointer',
    border: '1px solid transparent', width: w,
    whiteSpace: 'nowrap', opacity: disabled ? 0.5 : 1,
  };

  let style: React.CSSProperties;
  if (kind === 'primary') {
    style = { ...base, background: danger ? t.danger : t.accent, color: t.accentInk };
  } else if (kind === 'secondary') {
    style = { ...base, background: 'transparent', color: t.ink, borderColor: t.borderStrong };
  } else {
    style = { ...base, background: 'transparent', color: t.inkSoft };
  }

  return <button type={type} style={style} onClick={onClick} disabled={disabled}>{icon}{children}</button>;
}

// ── Pill ──
type PillTone = 'live' | 'enrolled' | 'notenrolled' | 'paid' | 'neutral' | 'danger' | 'warn';

export function Pill({ t, tone = 'neutral', children }: { t: NxTheme; tone?: PillTone; children: React.ReactNode }) {
  const tones: Record<string, { bg: string; fg: string; dot?: string }> = {
    live: { bg: withAlpha(t.success, t.dark ? 0.18 : 0.12), fg: t.success, dot: t.success },
    enrolled: { bg: withAlpha(t.accent, t.dark ? 0.18 : 0.10), fg: t.accent, dot: t.accent },
    notenrolled: { bg: t.paperAlt, fg: t.inkSoft, dot: t.inkMute },
    paid: { bg: withAlpha(t.success, t.dark ? 0.18 : 0.12), fg: t.success },
    neutral: { bg: t.paperAlt, fg: t.inkSoft },
    danger: { bg: withAlpha(t.danger, 0.12), fg: t.danger },
    warn: { bg: withAlpha(t.warning, 0.14), fg: t.warning },
  };
  const c = tones[tone] || tones.neutral;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      height: 22, padding: '0 9px', borderRadius: 999,
      background: c.bg, color: c.fg,
      fontSize: 11, fontWeight: 500, letterSpacing: '0.02em', whiteSpace: 'nowrap',
    }}>
      {c.dot && <span style={{ width: 5, height: 5, borderRadius: 99, background: c.dot }} />}
      {children}
    </span>
  );
}

// ── BrandPanel ── (dark geometric panel for login/signup)
export function BrandPanel({ t }: { t: NxTheme }) {
  const ink = '#fafaf8';
  return (
    <div style={{
      position: 'relative', width: '100%', height: '100%',
      background: t.ink, color: ink, overflow: 'hidden',
      display: 'flex', flexDirection: 'column', padding: 40, boxSizing: 'border-box',
    }}>
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: `radial-gradient(${withAlpha(ink, 0.07)} 1px, transparent 1px)`,
        backgroundSize: '22px 22px',
        maskImage: 'linear-gradient(135deg, black 30%, transparent 80%)',
        WebkitMaskImage: 'linear-gradient(135deg, black 30%, transparent 80%)',
      }} />
      <svg viewBox="0 0 400 400" preserveAspectRatio="none" style={{ position: 'absolute', right: -120, top: -80, width: 520, height: 520, opacity: 0.95 }}>
        <circle cx="200" cy="200" r="180" stroke={t.accent} strokeWidth="1" fill="none" />
        <circle cx="200" cy="200" r="120" stroke={withAlpha(ink, 0.18)} strokeWidth="1" fill="none" />
        <path d="M 20 200 A 180 180 0 0 1 380 200" stroke={t.accent} strokeWidth="22" fill="none" strokeLinecap="round" />
      </svg>
      <Wordmark t={{ ...t, ink } as NxTheme} color={ink} />
      <div style={{ flex: 1 }} />
      <div style={{ position: 'relative', maxWidth: 320 }}>
        <div style={{
          fontFamily: '"Instrument Serif", serif',
          fontSize: 38, lineHeight: 1.05, letterSpacing: '-0.01em',
          color: ink, fontWeight: 400,
        }}>
          Assessment, made <em style={{ color: t.accent, fontStyle: 'italic' }}>orderly</em>.
        </div>
        <div style={{ fontSize: 13, lineHeight: 1.55, color: withAlpha(ink, 0.62), marginTop: 14 }}>
          A quiet workspace for image-based clinical assessment.
        </div>
      </div>
    </div>
  );
}

// ── PageShell ──
export function PageShell({ t, children, style }: { t: NxTheme; children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{
      width: '100%', height: '100%',
      background: t.paper, color: t.ink,
      fontFamily: '"Geist", ui-sans-serif, system-ui, -apple-system, sans-serif',
      ...style,
    }}>
      {children}
    </div>
  );
}

// ── TopBar ──
interface CrumbItem {
  label: string;
  path?: string;
}

interface TopBarProps {
  t: NxTheme;
  crumbs?: (string | CrumbItem)[];
  right?: React.ReactNode;
  dense?: boolean;
  onNavigate?: (path: string) => void;
}

export function TopBar({ t, crumbs, right, dense, onNavigate }: TopBarProps) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      height: dense ? 56 : 64, padding: '0 28px',
      borderBottom: `1px solid ${t.border}`,
      background: t.surface, flexShrink: 0,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
        <Wordmark t={t} />
        {crumbs && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 6 }}>
            <span style={{ color: t.inkFaint, fontSize: 14 }}>/</span>
            {crumbs.map((c, i) => {
              const label = typeof c === 'string' ? c : c.label;
              const path = typeof c === 'string' ? undefined : c.path;
              const isLast = i === crumbs.length - 1;
              const isClickable = !isLast && path && onNavigate;
              return (
                <React.Fragment key={i}>
                  <span
                    onClick={isClickable ? () => onNavigate(path) : undefined}
                    style={{
                      fontSize: 13,
                      color: isLast ? t.ink : t.inkSoft,
                      fontWeight: isLast ? 500 : 400,
                      cursor: isClickable ? 'pointer' : 'default',
                      textDecoration: 'none',
                    }}
                  >{label}</span>
                  {!isLast && <span style={{ color: t.inkFaint, fontSize: 14 }}>/</span>}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        {right}
      </div>
    </div>
  );
}

// ── UserChip (dropdown with built-in logout confirmation) ──
export function UserChip({ t, name = 'User', email, onLogout }: { t: NxTheme; name?: string; email?: string; onLogout?: () => void }) {
  const [open, setOpen] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <>
      <div ref={ref} style={{ position: 'relative' }}>
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '4px 10px 4px 4px', borderRadius: 99,
            border: `1px solid ${t.border}`, cursor: 'pointer',
          }}
          onClick={() => setOpen(o => !o)}
        >
          <div style={{
            width: 28, height: 28, borderRadius: 99,
            background: t.paperAlt, color: t.inkSoft,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 600,
          }}>{initials}</div>
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
            <span style={{ fontSize: 12, fontWeight: 500, color: t.ink }}>{name}</span>
            {email && <span style={{ fontSize: 11, color: t.inkMute }}>{email}</span>}
          </div>
          {NxIcon.chevronDown(t.inkMute)}
        </div>

        {open && (
          <div style={{
            position: 'absolute', top: 'calc(100% + 6px)', right: 0,
            minWidth: 180, background: t.surface,
            border: `1px solid ${t.border}`, borderRadius: 10,
            boxShadow: '0 4px 16px rgba(0,0,0,0.10)', zIndex: 100,
            padding: '6px 0', overflow: 'hidden',
          }}>
            <div style={{ padding: '10px 14px', borderBottom: `1px solid ${t.border}` }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: t.ink }}>{name}</div>
              {email && <div style={{ fontSize: 11, color: t.inkMute, marginTop: 2 }}>{email}</div>}
            </div>
            <button
              onClick={() => { setOpen(false); setShowConfirm(true); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                width: '100%', padding: '10px 14px', border: 'none',
                background: 'transparent', cursor: 'pointer',
                fontSize: 13, color: t.danger, fontFamily: 'inherit',
                textAlign: 'left',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = t.paperAlt)}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              {NxIcon.logout(t.danger)} Logout
            </button>
          </div>
        )}
      </div>

      {/* Logout confirmation modal */}
      {showConfirm && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.4)', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          fontFamily: '"Geist", ui-sans-serif, system-ui, -apple-system, sans-serif',
        }} onClick={() => setShowConfirm(false)}>
          <div
            style={{
              background: t.surface, borderRadius: 14,
              padding: '28px 32px', maxWidth: 380, width: '90%',
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontSize: 16, fontWeight: 600, color: t.ink, marginBottom: 8 }}>
              Confirm Logout
            </div>
            <div style={{ fontSize: 13, color: t.inkSoft, lineHeight: 1.5, marginBottom: 24 }}>
              Are you sure you want to logout? Any unsaved progress may be lost.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <NxButton t={t} kind="secondary" onClick={() => setShowConfirm(false)}>Cancel</NxButton>
              <NxButton t={t} kind="primary" danger onClick={() => { setShowConfirm(false); onLogout?.(); }}>Logout</NxButton>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── PaletteSwatch ── (for instructions page)
export function PaletteSwatch({ kind, t }: { kind: 'unanswered' | 'answered' | 'review' | 'both'; t: NxTheme }) {
  const sw: Record<string, { bg: string; border: string; fg: string }> = {
    unanswered: { bg: t.paperAlt, border: t.borderStrong, fg: t.inkSoft },
    answered: { bg: withAlpha(t.success, 0.12), border: t.success, fg: t.success },
    review: { bg: withAlpha(t.warning, 0.14), border: t.warning, fg: t.warning },
    both: { bg: withAlpha(t.accent, 0.12), border: t.accent, fg: t.accent },
  };
  const s = sw[kind];
  return (
    <div style={{
      width: 28, height: 28, borderRadius: 6,
      background: s.bg, border: `1.25px solid ${s.border}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 12, fontWeight: 600, color: s.fg,
      fontFamily: '"Geist Mono", ui-monospace, monospace',
    }}>5</div>
  );
}

// ── PaletteBtn ── (for exam sidebar)
export function PaletteBtn({ status, n, t, active, onClick }: {
  status: 'unanswered' | 'answered' | 'review' | 'both';
  n: number; t: NxTheme; active?: boolean; onClick?: () => void;
}) {
  const sw: Record<string, { bg: string; fg: string; border: string }> = {
    unanswered: { bg: t.dark ? t.surfaceAlt : '#f0eee9', fg: t.inkSoft, border: t.border },
    answered: { bg: withAlpha(t.success, t.dark ? 0.22 : 0.14), fg: t.success, border: withAlpha(t.success, 0.35) },
    review: { bg: withAlpha(t.warning, t.dark ? 0.22 : 0.16), fg: t.warning, border: withAlpha(t.warning, 0.35) },
    both: { bg: withAlpha(t.accent, t.dark ? 0.25 : 0.14), fg: t.accent, border: withAlpha(t.accent, 0.4) },
  };
  const s = sw[status];
  return (
    <button
      onClick={onClick}
      style={{
        width: 36, height: 36, borderRadius: 8,
        background: s.bg, color: s.fg,
        border: `${active ? 1.5 : 1}px solid ${active ? t.ink : s.border}`,
        fontFamily: '"Geist Mono", ui-monospace, monospace',
        fontSize: 13, fontWeight: 500, cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        position: 'relative',
        boxShadow: active ? `0 0 0 3px ${withAlpha(t.ink, 0.06)}` : 'none',
      }}
    >
      {n}
      {(status === 'review' || status === 'both') && (
        <span style={{
          position: 'absolute', top: 3, right: 3,
          width: 5, height: 5, borderRadius: 99,
          background: status === 'review' ? t.warning : t.accent,
        }} />
      )}
    </button>
  );
}
